<script setup>
import { computed, reactive, ref, getCurrentInstance } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import MainLayout from '@/layout/main/MainLayout.vue';
import Card from 'primevue/card';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { useToast } from 'primevue/usetoast';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useEnhancedLoading } from '@/composables/useEnhancedLoading';
import ContentModal from '@/components/ContentModal.vue';
import PaymentMethodModal from '@/components/modals/PaymentMethodModal.vue';
import WiseTransferInstructions from '@/components/payment/WiseTransferInstructions.vue';
import PixInstructions from '@/components/payment/PixInstructions.vue';
import BoletoInstructions from '@/components/payment/BoletoInstructions.vue';
import BoletoPayerForm from '@/components/payment/BoletoPayerForm.vue';
import axios from 'axios';

defineOptions({
    layout: MainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showPageLoading, hidePageLoading } = useEnhancedLoading();

const props = defineProps({
    orderData: {
        type: Object,
        required: true
    },
    languages: {
        type: Array,
        default: () => []
    },
    canUseDeferredPayment: {
        type: Boolean,
        default: false
    }
});

const { formatWithSymbol, formatConvertedPrice, getCurrentCurrency, onCurrencyChange, convertPrice } = useCurrencyFormatter();
const currentCurrency = ref(getCurrentCurrency());

const submitting = ref(false);
const showPaymentModal = ref(false);
const pendingOrderPayload = ref(null);
const wiseOrderData = ref(null);
const showWiseInstructions = ref(false);
const pixOrderData = ref(null);
const showPixInstructions = ref(false);
const boletoOrderData = ref(null);
const showBoletoInstructions = ref(false);
const showBoletoPayerForm = ref(false);

const showContentModal = ref(false);
const selectedItems = ref([]);
const siteFeatures = ref(null);
const selectedOrderItem = ref(null);
const submittingContent = ref(false);
const currentItemIndex = ref(null);

const itemContents = reactive({});

const openContentModal = (itemIndex) => {
    const item = props.orderData.items[itemIndex];

    currentItemIndex.value = itemIndex;
    selectedItems.value = [item];

    siteFeatures.value = {
        menu: item.menu,
        banner: item.banner,
        promo: item.promo,
        google_news: item.google_news,
        embed: item.embed,
        restrict: item.restrict,
        restrict_detail: item.restrict_detail,
        archive_article: item.archive_article,
        archive_due: item.archive_due,
        images: item.images,
    };

    const selectedType = item.types.find(t => t.id === item.type_id);
    const acceptedAnchors = selectedType?.accepted_anchors || 3;

    selectedOrderItem.value = {
        accepted_anchors: acceptedAnchors,
        url: item.url,
        type_name: selectedType?.name
    };

    showContentModal.value = true;
};

const closeContentModal = () => {
    showContentModal.value = false;
    selectedItems.value = [];
    siteFeatures.value = null;
    selectedOrderItem.value = null;
    currentItemIndex.value = null;
};

const onContentSubmit = async (contentData) => {
    if (currentItemIndex.value !== null) {
        itemContents[currentItemIndex.value] = { ...contentData };

        toast.add({
            severity: 'success',
            summary: $t('Success'),
            detail: $t('Content saved successfully'),
            life: 3000
        });

        closeContentModal();
    }
};

const maxAnchorsComputed = computed(() => {
    if (selectedItems.value.length === 0) return 1;
    return Math.max(...selectedItems.value.map(item => {
        const selectedType = item.types.find(t => t.id === item.type_id);
        return selectedType?.accepted_anchors || 1;
    }));
});

const allItemsHaveContent = computed(() => {
    return props.orderData.items.every((item, index) => {
        return itemContents[index] && Object.keys(itemContents[index]).length > 0;
    });
});

const canSubmitOrder = computed(() => {
    return allItemsHaveContent.value;
});

const subtotalPrice = computed(() => {
    return props.orderData.items.reduce((sum, item) => {
        const converted = convertPrice(item.selectedPrice, item.selectedCoin, currentCurrency.value);
        const rounded = Math.ceil(converted * 20) / 20;
        return sum + (rounded * item.quantity);
    }, 0);
});

const paymentTaxAmount = computed(() => {
    return subtotalPrice.value * (props.orderData.paymentTaxPercentage / 100);
});

const totalPrice = computed(() => {
    return subtotalPrice.value + paymentTaxAmount.value;
});

const buildOrderPayload = () => {
    const itemsWithContent = props.orderData.items.map((item, index) => ({
        site_id: item.site_id,
        quantity: item.quantity,
        type_id: item.type_id,
        observations: item.observations || '',
        content_data: itemContents[index] || null
    }));

    return {
        items: itemsWithContent,
        selected_currency: currentCurrency.value,
        deferred_payment: props.orderData.deferred_payment || false,
    };
};

const submitOrder = () => {
    if (!canSubmitOrder.value) {
        toast.add({
            severity: 'warn',
            summary: $t('Warning'),
            detail: $t('Please add content for all items before submitting'),
            life: 3000
        });
        return;
    }

    const payload = buildOrderPayload();

    if (props.orderData.deferred_payment) {
        submitting.value = true;
        router.post(proxy.route('client.checkout.store-with-content'), payload, {
            onSuccess: () => {},
            onError: (errors) => {
                console.error('Order submission errors:', errors);
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors.message || $t('Failed to create order. Please try again.'),
                    life: 5000
                });
                submitting.value = false;
            }
        });
    } else {
        pendingOrderPayload.value = payload;
        showPaymentModal.value = true;
    }
};

const onPaymentConfirm = async (method) => {
    if (method === 'stripe') {
        submitting.value = true;
        showPageLoading($t('Redirecting to payment...'));
        try {
            const payload = { ...pendingOrderPayload.value, payment_method: method };
            const response = await axios.post(
                proxy.route('client.checkout.create-stripe-session'),
                payload
            );
            window.location.href = response.data.checkout_url;
        } catch (error) {
            console.error('Stripe session error:', error);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: error.response?.data?.message || $t('Failed to initialize payment. Please try again.'),
                life: 5000
            });
            submitting.value = false;
            hidePageLoading();
            showPaymentModal.value = false;
        }
    } else if (method === 'wise') {
        submitting.value = true;
        showPageLoading($t('Creating Wise order...'));
        try {
            const payload = { ...pendingOrderPayload.value, payment_method: 'wise' };
            const response = await axios.post(
                proxy.route('client.checkout.create-wise-order'),
                payload
            );
            wiseOrderData.value = response.data;
            showPaymentModal.value = false;
            showWiseInstructions.value = true;
        } catch (error) {
            console.error('Wise order error:', error);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: error.response?.data?.message || $t('Failed to create order. Please try again.'),
                life: 5000
            });
        } finally {
            submitting.value = false;
            hidePageLoading();
        }
    } else if (method === 'pix') {
        submitting.value = true;
        showPageLoading($t('Generating Pix QR Code...'));
        try {
            const payload = { ...pendingOrderPayload.value, payment_method: 'pix', selected_currency: 'BRL' };
            const response = await axios.post(
                proxy.route('client.checkout.create-pix-order'),
                payload
            );
            pixOrderData.value = response.data;
            showPaymentModal.value = false;
            showPixInstructions.value = true;
        } catch (error) {
            console.error('Pix order error:', error);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: error.response?.data?.message || $t('Failed to create order. Please try again.'),
                life: 5000
            });
        } finally {
            submitting.value = false;
            hidePageLoading();
        }
    } else if (method === 'boleto') {
        showPaymentModal.value = false;
        showBoletoPayerForm.value = true;
    } else if (method === 'manual') {
        submitting.value = true;
        showPageLoading($t('Processing payment...'));
        try {
            const payload = { ...pendingOrderPayload.value, payment_method: 'manual' };
            const response = await axios.post(
                proxy.route('client.checkout.store-with-content'),
                payload
            );
            showPaymentModal.value = false;
            router.visit(response.data.redirect);
        } catch (error) {
            console.error('Manual order error:', error);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: error.response?.data?.message || $t('Failed to create order. Please try again.'),
                life: 5000
            });
        } finally {
            submitting.value = false;
            hidePageLoading();
        }
    }
};

const onBoletoPayerConfirm = async (payerData) => {
    submitting.value = true;
    showPageLoading($t('Generating Boleto...'));
    try {
        const payload = {
            ...pendingOrderPayload.value,
            payment_method: 'boleto',
            selected_currency: 'BRL',
            payer_first_name: payerData.first_name,
            payer_last_name: payerData.last_name,
            payer_cpf: payerData.cpf,
            payer_zip: payerData.zip,
            payer_street: payerData.street,
            payer_number: payerData.number,
            payer_neighborhood: payerData.neighborhood,
            payer_city: payerData.city,
            payer_state: payerData.state,
        };
        const response = await axios.post(
            proxy.route('client.checkout.create-boleto-order'),
            payload
        );
        boletoOrderData.value = response.data;
        showBoletoPayerForm.value = false;
        showBoletoInstructions.value = true;
    } catch (error) {
        console.error('Boleto order error:', error);
        toast.add({
            severity: 'error',
            summary: $t('Error'),
            detail: error.response?.data?.message || $t('Failed to create order. Please try again.'),
            life: 5000
        });
    } finally {
        submitting.value = false;
        hidePageLoading();
    }
};

const formatLinkType = (linkType) => {
    if (!linkType) return 'NOFOLLOW';

    const normalizedType = linkType.toUpperCase().trim();

    if (normalizedType.includes('DOFOLLOW')) {
        return 'DOFOLLOW';
    }

    if (normalizedType.includes('SPONSOR')) {
        return 'SPONSORED';
    }

    return 'NOFOLLOW';
};

// Listen for currency changes
onCurrencyChange(({ currency }) => {
    currentCurrency.value = currency;
});
</script>

<template>
    <div class="place-order-page">

    <!-- Wise Transfer Instructions (substitui o conteúdo da página) -->
    <div v-if="showWiseInstructions && wiseOrderData">
        <WiseTransferInstructions
            :order-number="wiseOrderData.order_number"
            :bank-details="wiseOrderData.bank_details"
            :total="wiseOrderData.total"
            :currency="wiseOrderData.currency"
            :order-id="wiseOrderData.order_id"
        />
    </div>

    <!-- Pix Instructions -->
    <div v-else-if="showPixInstructions && pixOrderData">
        <PixInstructions
            :order-number="pixOrderData.order_number"
            :qr-code-base64="pixOrderData.qr_code_base64"
            :qr-code-text="pixOrderData.qr_code_text"
            :expiration-date="pixOrderData.expiration_date"
            :total="pixOrderData.total"
            :currency="pixOrderData.currency"
            :order-id="pixOrderData.order_id"
        />
    </div>

    <!-- Boleto Instructions -->
    <div v-else-if="showBoletoInstructions && boletoOrderData">
        <BoletoInstructions
            :order-number="boletoOrderData.order_number"
            :boleto-url="boletoOrderData.boleto_url"
            :barcode="boletoOrderData.barcode"
            :expiration-date="boletoOrderData.expiration_date"
            :total="boletoOrderData.total"
            :currency="boletoOrderData.currency"
            :order-id="boletoOrderData.order_id"
        />
    </div>

    <template v-else>
    <!-- Simple Header with Back Button -->
    <div class="mb-6 flex items-center justify-between">
        <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-surface-900 dark:text-surface-0">
                {{ $t('Place Order') }}
            </h1>
            <p class="text-surface-600 dark:text-surface-400 text-sm mt-1">{{ $t('Review your items and add content for each one') }}</p>
        </div>
        <Button
            :label="$t('Back')"
            icon="pi pi-arrow-left"
            severity="secondary"
            text
            @click="() => {
                try {
                    const routeName = proxy.route ? proxy.route('client.checkout.index') : '/client/checkout';
                    router.visit(routeName);
                } catch (e) { console.error(e); }
            }"
        />
    </div>
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <!-- Order Items Table -->
            <div class="lg:col-span-3">
                <Card>
                    <template #title>
                        {{ $t('Order Items') }}
                    </template>
                    <template #content>
                        <DataTable
                            :value="orderData.items"
                            class="checkout-table"
                            :paginator="false"
                            stripedRows
                        >
                            <Column field="url" :header="$t('Site URL')" style="min-width: 200px">
                                <template #body="{ data }">
                                    <span class="font-medium">{{ data.url }}</span>
                                </template>
                            </Column>

                            <Column field="type" :header="$t('Type')" style="min-width: 150px">
                                <template #body="{ data }">
                                    <span class="font-medium">
                                        {{ data.types.find(t => t.id === data.type_id)?.name || '--' }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="link_type" :header="$t('Link Type')" style="min-width: 120px">
                                <template #body="{ data }">
                                    <span class="font-medium">{{ formatLinkType(data.link_type) }}</span>
                                </template>
                            </Column>

                            <Column field="observations" :header="$t('Observations')" style="min-width: 150px">
                                <template #body="{ data }">
                                    <span class="text-surface-600">{{ data.observations || '-' }}</span>
                                </template>
                            </Column>

                            <Column field="price" :header="$t('Price')" style="min-width: 120px">
                                <template #body="{ data }">
                                    <span class="font-semibold">
                                        {{ formatConvertedPrice(data.selectedPrice, data.selectedCoin) }}
                                    </span>
                                </template>
                            </Column>

                            <Column field="actions" :header="$t('Actions')" style="min-width: 140px">
                                <template #body="{ data, index }">
                                    <Button
                                        :label="itemContents[index] ? $t('Edit Content') : $t('Add Content')"
                                        :icon="itemContents[index] ? 'pi pi-pencil' : 'pi pi-plus'"
                                        :severity="itemContents[index] ? 'secondary' : 'primary'"
                                        size="small"
                                        @click="openContentModal(index)"
                                    />
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </div>

            <!-- Order Summary -->
            <div class="lg:col-span-1">
                <Card>
                    <template #title>
                        {{ $t('Order Summary') }}
                    </template>
                    <template #content>
                        <div class="space-y-4">
                            <!-- Summary -->
                            <div class="space-y-3">
                                <div class="flex justify-between">
                                    <span class="text-surface-600">{{ $t('Items') }}:</span>
                                    <span>{{ orderData.items.length }}</span>
                                </div>

                                <div class="flex justify-between">
                                    <span class="text-surface-600">{{ $t('Subtotal') }}:</span>
                                    <span class="font-medium whitespace-nowrap">{{ formatWithSymbol(subtotalPrice, currentCurrency) }}</span>
                                </div>

                                <div v-if="paymentTaxAmount > 0" class="flex justify-between">
                                    <span class="text-surface-600">{{ $t('Payment Tax') }} ({{ orderData.paymentTaxPercentage }}%):</span>
                                    <span class="whitespace-nowrap">{{ formatWithSymbol(paymentTaxAmount, currentCurrency) }}</span>
                                </div>

                                <div class="border-t pt-3">
                                    <div class="flex justify-between">
                                        <span class="text-lg font-semibold">{{ $t('Total') }}:</span>
                                        <span class="text-lg font-bold text-primary whitespace-nowrap">
                                            {{ formatWithSymbol(totalPrice, currentCurrency) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <!-- Content Progress -->
                            <div class="mt-6 p-4 bg-surface-50 rounded-lg">
                                <h4 class="font-medium mb-3">{{ $t('Content Progress') }}</h4>
                                <div class="space-y-2">
                                    <div class="flex justify-between text-sm">
                                        <span>{{ $t('Items with content') }}:</span>
                                        <span>{{ Object.keys(itemContents).length }} / {{ orderData.items.length }}</span>
                                    </div>
                                    <div class="w-full bg-surface-200 rounded-full h-2">
                                        <div
                                            class="bg-primary h-2 rounded-full transition-all duration-300"
                                            :style="{ width: `${(Object.keys(itemContents).length / orderData.items.length) * 100}%` }"
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            <!-- Submit Button -->
                            <Button
                                :label="orderData.deferred_payment ? $t('Submit Order') : $t('Proceed to Payment')"
                                :icon="orderData.deferred_payment ? 'pi pi-check' : 'pi pi-lock'"
                                :loading="submitting"
                                :disabled="!canSubmitOrder"
                                @click="submitOrder"
                                class="w-full mt-6"
                                size="large"
                            />

                            <p v-if="!canSubmitOrder" class="text-sm text-center text-surface-500 mt-2">
                                {{ $t('Please add content for all items to submit your order') }}
                            </p>
                        </div>
                    </template>
                </Card>
            </div>
        </div>
    </template>

    </div>

    <!-- Payment Method Modal -->
    <PaymentMethodModal
        v-model:visible="showPaymentModal"
        :submitting="submitting"
        :subtotal="subtotalPrice"
        :currency="currentCurrency"
        :payment-taxes="orderData.paymentTaxes ?? {}"
        @confirm="onPaymentConfirm"
    />

    <!-- Boleto Payer Form -->
    <BoletoPayerForm
        v-model:visible="showBoletoPayerForm"
        :submitting="submitting"
        @confirm="onBoletoPayerConfirm"
    />

    <!-- Content Modal -->
    <ContentModal
        v-model:visible="showContentModal"
        :header="currentItemIndex !== null ? (itemContents[currentItemIndex] ? $t('Edit Content') : $t('Add Content')) : $t('Add Content')"
        :site-features="siteFeatures"
        :max-anchors="maxAnchorsComputed"
        :selected-order-item="selectedOrderItem"
        :languages="props.languages"
        :submitting="submittingContent"
        :initial-data="currentItemIndex !== null ? (itemContents[currentItemIndex] || {}) : {}"
        @submit="onContentSubmit"
        @close="closeContentModal"
    />
</template>

<style scoped>
.place-order-page {
    padding: 1.5rem;
    margin: 0 auto;
}

/* DataTable Responsive Styles */
.checkout-table {
    font-size: 0.95rem;
}

.checkout-table :deep(.p-datatable-wrapper) {
    overflow-x: auto;
}

.checkout-table :deep(.p-datatable-thead > tr > th) {
    background-color: var(--surface-50);
    font-weight: 600;
    padding: 1rem 0.75rem;
}

.checkout-table :deep(.p-datatable-tbody > tr > td) {
    padding: 1rem 0.75rem;
    vertical-align: top;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .place-order-page {
        padding: 1rem;
    }

    .checkout-table {
        font-size: 0.875rem;
    }

    .checkout-table :deep(.p-datatable-thead > tr > th),
    .checkout-table :deep(.p-datatable-tbody > tr > td) {
        padding: 0.75rem 0.5rem;
    }
}

/* Extra small screens - stack columns */
@media (max-width: 640px) {
    .checkout-table :deep(.p-datatable-wrapper) {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .checkout-table :deep(table) {
        min-width: 900px;
    }
}
</style>
